import { Button, Flex, Form, Modal, Skeleton } from "antd"
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  LeadProjectIdCard,
  type LeadProjectIdFormData
} from "../components/settings-configuration/LeadProjectIdCard.tsx"
import {
  fetchIdConfiguration,
  updateIdConfiguration
} from "../redux/settings.thunks.ts"

const DEFAULTS: LeadProjectIdFormData = { mode: "incremental", start_from: 1 }

export default function SettingsConfigurationPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const config = useAppSelector((state) => state.settings.idConfiguration)
  const fetching = useAppSelector(
    (state) => state.settings.idConfigurationFetching
  )
  const saving = useAppSelector((state) => state.settings.idConfigurationSaving)

  const [form] = Form.useForm<LeadProjectIdFormData>()
  const [dirty, setDirty] = useState(false)

  const locked = config?.start_locked === true

  const initialValues = useMemo<LeadProjectIdFormData>(
    () => ({
      mode: config?.mode ?? DEFAULTS.mode,
      start_from: config?.start_from ?? DEFAULTS.start_from
    }),
    [config]
  )

  // Layout effect, not a plain one: dispatching before paint means
  // `idConfigurationFetching` flips true before the browser ever shows a
  // frame, so the form's DEFAULTS never flash before the loading skeleton.
  useLayoutEffect(() => {
    generateDocumentTitle("Settings Configurations")
    void dispatch(fetchIdConfiguration())
  }, [dispatch])

  useEffect(() => {
    // Only resync from the server while the form has no unsaved edit of its
    // own — otherwise a slow initial fetch resolving after the user has
    // already started typing would silently discard what they typed.
    if (!dirty) form.setFieldsValue(initialValues)
  }, [form, initialValues, dirty])

  const persist = async (values: LeadProjectIdFormData) => {
    const payload =
      values.mode === "incremental"
        ? { mode: values.mode, start_from: values.start_from }
        : { mode: values.mode }
    try {
      await dispatch(updateIdConfiguration(payload)).unwrap()
      setDirty(false)
    } catch {
      // errors surface through the shared response handler
    }
  }

  const handleFinish = (values: LeadProjectIdFormData) => {
    const willLock = !locked && values.mode === "incremental"
    if (willLock) {
      Modal.confirm({
        title: t("settingsConfiguration.confirmLock.title"),
        content: t("settingsConfiguration.confirmLock.content"),
        okText: t("settingsConfiguration.confirmLock.okText"),
        onOk: () => persist(values)
      })
      return
    }
    void persist(values)
  }

  if (fetching && !config) {
    return (
      <Flex flex={1}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Flex>
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      className="flex-1 flex gap-3 flex-col w-full with-default-error"
      initialValues={initialValues}
      disabled={saving}
      onValuesChange={() => setDirty(true)}
      onFinish={handleFinish}
    >
      <div className="space-y-3 flex-1">
        <LeadProjectIdCard form={form} locked={locked} />
      </div>

      <Flex
        justify="end"
        className="lg:flex-row max-lg:items-center sticky bottom-0 bg-background py-2"
      >
        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          disabled={!dirty}
        >
          {t("settingsConfiguration.save")}
        </Button>
      </Flex>
    </Form>
  )
}
